import { Fragment, Suspense, lazy, useEffect, useId, useRef, useState } from 'react';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import type { Column, Project } from '@vd/types';
import { useNavigate, useParams, useSearchParams } from 'react-router-dom';
import { Button, FormField, Input, Tooltip, cn, useToast } from '@vd/ui';
import { api } from '../../lib/api';
import { useCurrentCompany } from '../../features/auth/AuthProvider';
import { useGuardedNavigate, useNavigationConfirmation } from '../../app/NavigationGuard';
import { ProjectColorPicker } from '../../features/projects/ProjectColorPicker';
import { UrlPatternHelp } from '../../features/projects/UrlPatternHelp';
import { Trash2, Check, X, Plus, Pencil, ArrowLeft, GripVertical } from 'lucide-react';
import {
  DndContext,
  DragOverlay,
  PointerSensor,
  closestCenter,
  useSensor,
  useSensors,
  type DragEndEvent,
  type DragStartEvent,
} from '@dnd-kit/core';
import {
  SortableContext,
  arrayMove,
  horizontalListSortingStrategy,
  useSortable,
} from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';
import { createPortal } from 'react-dom';
import { DestructiveConfirm } from '../../features/modals/DestructiveConfirm';
import { InlineProjectName } from '../../features/projects/InlineProjectName';
import { WalkthroughRecordingTab } from '../../features/projects/WalkthroughRecordingTab';
import { ExtensionCaptureTab } from '../../features/projects/ExtensionCaptureTab';
import { saveProjectRecordingMode } from '../../features/projects/saveRecordingMode';
import type { WidgetSection } from './tabs/WidgetTab';
import { IntakeTab } from './tabs/IntakeTab';
import { IntakeBoardDrawing } from './tabs/IntakeBoardDrawing';
import { BigSwitch } from '../../features/settings/BigSwitch';
import { ProjectTicketTypesTab } from './tabs/ProjectTicketTypesTab';
import { JiraMappingTab } from './tabs/JiraMappingTab';
import styles from './Settings.module.css';
import pageStyles from './ProjectSettingsPage.module.css';
import { WidgetNudge } from '../../features/widget/WidgetNudge';
import { webLink } from '../../features/ticket/TicketFixes';
import { EVERY_SCREEN, PROJECT_SETTINGS_SCREEN, WIDGET_SETTINGS_SCREEN } from '../../lib/screenNames';
import { ProjectMark } from '../../features/projects/ProjectMark';
import { ProjectIconField } from '../../features/projects/ProjectIconField';
import { readIconSoon } from '../../features/projects/iconLook';
import { useTabProject, useTabTitle, useTabWorkspaceName } from '../../lib/tabTitle';
import { BoardWorkflow } from '../../features/workflow/BoardWorkflow';
import { MAX_COLUMNS } from './columnLimit';

// The widget's settings load when first opened (23 Oct): their launcher CSS
// check uses PostCSS, which would otherwise ride in every page's bundle and,
// in dev, fill each page's console with Vite's warnings about the Node
// modules PostCSS reads.
const WidgetTab = lazy(() => import('./tabs/WidgetTab').then((module) => ({ default: module.WidgetTab })));

// The four widget-* tabs render one mounted WidgetTab in controlled
// mode - splitting the old single Widget tab (TOADO-257) without
// duplicating its staged-draft save machinery. 'widget' (no suffix)
// stays valid as the Install panel so existing ?tab=widget deep links
// (home checklist, URLs-tab nudge) keep landing somewhere sensible.
type Tab =
  | 'general' | 'columns' | 'workflow' | 'ticket-types' | 'intake' | 'urls' | 'recording' | 'extension'
  | 'widget' | 'widget-appearance' | 'widget-questions' | 'widget-capture' | 'widget-security'
  | 'jira' | 'archival' | 'danger';

/**
 * The headings the nav groups under, and the only place their names live.
 *
 * Two of them now, which is why this is a map: URL patterns and Allowed sites
 * are both lists of URLs on a project and read as the same kind of setting
 * until something says which half of the product each belongs to. They do
 * different jobs - patterns decide which project a capture files into, and
 * the allowlist decides which sites may use a widget key - and only one of
 * them is a security boundary.
 */
const GROUP_LABEL: Record<'extension' | 'widget', string> = {
  extension: 'Extension',
  widget: 'Bug Capture Widget',
};

const WIDGET_SECTION_BY_TAB: Partial<Record<Tab, WidgetSection>> = {
  'widget': 'install',
  'widget-appearance': 'appearance',
  'widget-questions': 'questions',
  'widget-capture': 'capture',
  'widget-security': 'security',
};

/**
 * Each tab's screen name, for pins (see `lib/screenNames.ts`). Every tab is at
 * this one address, so the name on the tab's region is how the extension knows
 * which tab a pin was placed on. Written out rather than built from the tab
 * keys, because a name is stored with every pin and must outlive a key being
 * renamed. The widget tabs share one name: they share one editor, which names
 * its own sections.
 */
const TAB_SCREEN: Record<Tab, string> = {
  'general': `${PROJECT_SETTINGS_SCREEN}/general`,
  'columns': `${PROJECT_SETTINGS_SCREEN}/columns`,
  'workflow': `${PROJECT_SETTINGS_SCREEN}/workflow`,
  'ticket-types': `${PROJECT_SETTINGS_SCREEN}/ticket-types`,
  'intake': `${PROJECT_SETTINGS_SCREEN}/intake`,
  'urls': `${PROJECT_SETTINGS_SCREEN}/urls`,
  'recording': `${PROJECT_SETTINGS_SCREEN}/recording`,
  'extension': `${PROJECT_SETTINGS_SCREEN}/extension`,
  'widget': WIDGET_SETTINGS_SCREEN,
  'widget-appearance': WIDGET_SETTINGS_SCREEN,
  'widget-questions': WIDGET_SETTINGS_SCREEN,
  'widget-capture': WIDGET_SETTINGS_SCREEN,
  'widget-security': WIDGET_SETTINGS_SCREEN,
  'jira': `${PROJECT_SETTINGS_SCREEN}/jira`,
  'archival': `${PROJECT_SETTINGS_SCREEN}/archival`,
  'danger': `${PROJECT_SETTINGS_SCREEN}/danger`,
};

/** Every tab a `?tab=` link may name. */
const TABS = Object.keys(TAB_SCREEN) as Tab[];

function isTab(value: string): value is Tab {
  return (TABS as string[]).includes(value);
}

/** The widget tabs and Jira, matching requireOwner on their routes. */
function isOwnerOnly(tab: Tab): boolean {
  return tab.startsWith('widget') || tab === 'jira';
}

interface LocalColumn { id: string; name: string; isIntake?: boolean; isTerminal?: boolean; waitingOnReporter?: boolean; }

/** The columns as edited are the columns as saved: same order, names and marks. */
function sameColumns(local: LocalColumn[], saved: Column[] | undefined): boolean {
  if (!saved || local.length !== saved.length) return false;
  return local.every((column, index) => {
    const other = saved[index]!;
    return column.id === other.id && column.name === other.name
      && (column.waitingOnReporter === true) === (other.waitingOnReporter === true);
  });
}

/**
 * One project's settings per instance (TOADO-977 review round 16): a switch of project
 * starts afresh, so nothing still out for one (a waiting-stage answer, its busy flag, a
 * column draft) reaches the other's.
 */
export function ProjectSettingsPage() {
  const { projectId } = useParams();
  return <ProjectSettings key={projectId} />;
}

function ProjectSettings() {
  // This tab's title and icon: the project's mark (TOADO-905).
  const tabWorkspace = useTabWorkspaceName();
  const tabProject = useTabProject(useParams<{ projectId: string }>().projectId);
  useTabTitle({ parts: [tabProject?.name, "Settings", tabWorkspace], project: tabProject });
  const { projectId } = useParams();
  const navigate = useNavigate();
  const qc = useQueryClient();
  const toast = useToast();
  const [searchParams, setSearchParams] = useSearchParams();
  const [tab, setTab] = useState<Tab>('general');
  /**
   * The setup wizard (TOADO-579) is opened from the page header on the widget
   * sections (Jason, 9 Oct: beside the project's name, not inside the
   * section). Each press is a new request the widget settings act on; their
   * unsaved changes, which the wizard's saves would collide with, hold it.
   */
  const [wizardRequest, setWizardRequest] = useState(0);
  const [widgetDirty, setWidgetDirty] = useState(false);
  /** Where the widget editor puts Try it live and its status, in this header. */
  const [widgetHeaderSlot, setWidgetHeaderSlot] = useState<HTMLElement | null>(null);
  // The Widget tab is owner-only, matching requireOwner on every widget route.
  const company = useCurrentCompany();
  const isOwner = company?.role === 'owner';
  const confirmNavigation = useNavigationConfirmation();
  const guardedNavigate = useGuardedNavigate();
  const navTitleId = useId();
  /**
   * Show a tab and name it in the address (`?tab=`, replacing the entry), so a
   * reload or a shared link opens the same tab rather than General. `extra`
   * sets further parameters in the same write: two writes in one render would
   * each start from the same parameters, and the second would drop the first.
   */
  const showTab = (next: Tab, extra: Record<string, string> = {}) => {
    setTab(next);
    const params = new URLSearchParams(searchParams);
    params.set('tab', next);
    for (const [key, value] of Object.entries(extra)) params.set(key, value);
    setSearchParams(params, { replace: true });
  };
  /**
   * Leaving the widget's sections unmounts its editor, so unsaved changes there
   * are asked about first (review of 0.2.0, 3 Oct); moving between its sections
   * keeps the editor, and its changes, where they are.
   */
  const leavesWidget = (next: Tab) => WIDGET_SECTION_BY_TAB[tab] !== undefined && WIDGET_SECTION_BY_TAB[next] === undefined;
  const selectTab = (next: Tab) => {
    if (leavesWidget(next) && !confirmNavigation()) return;
    showTab(next);
  };
  /** "Set up the widget" from the extension's sections: the widget, with its setup wizard open (TOADO-579). */
  const openWidgetSetup = () => showTab('widget', { setup: '1' });

  // Honor `?tab=`: a deep link (the "Add the widget" entry point on the home
  // checklist), a shared link, or a reload, since every tab change writes it.
  // Validated against the tabs and the owner gate, so a non-owner link to an
  // owner-only tab falls back to General, and the address is rewritten to say
  // so. Gated on `company` being loaded, not just mount, because on a cold
  // hard-refresh straight to this URL the auth context can resolve a beat
  // later, and judging the param before the role is known would strand an
  // owner on General. Depending on `searchParams` as well means a same-route
  // navigation that changes `?tab=` while this page is mounted is honored too.
  // The param stays in the address, so this runs again after every tab
  // change, and finds the tab it names already showing.
  useEffect(() => {
    if (!company) return;
    const requested = searchParams.get('tab');
    // A link to the bare settings address while a tab other than General is
    // showing: the tab stays (leaving it could drop unsaved widget changes),
    // and the address is made to name it again, so a copied link opens it.
    if (!requested) {
      if (tab !== 'general') showTab(tab);
      return;
    }
    if (isTab(requested) && (!isOwnerOnly(requested) || isOwner)) {
      // Not asked about here: a link or Back that would leave unsaved widget
      // settings has already been asked about by the app's navigation guard
      // (`NavigationGuard`), and asking again would ask twice.
      setTab(requested);
      return;
    }
    showTab('general');
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [company, searchParams]);

  // Review round 1 (Codex): the owner gate held at entry (nav + deep
  // link) but not for the duration - a role change while sitting on a
  // widget tab left the panel rendered. The API still authorizes every
  // widget call; this closes the UI surface too. Guarded on `company`
  // being loaded so a transient refetch cannot kick a real owner off
  // the tab.
  useEffect(() => {
    if (company && !isOwner && isOwnerOnly(tab)) showTab('general');
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [company, isOwner, tab]);

  const { data: project } = useQuery({
    queryKey: ['project', projectId],
    queryFn: () => api.getProject(projectId!),
    enabled: !!projectId,
  });
  /** This person's unsaved instructions for agents (TOADO-998), held here so switching tabs keeps them. */
  const [agentDraft, setAgentDraft] = useState<string | null>(null);
  /** A save of them out (review round 3), held here for the same reason. */
  const [agentSaving, setAgentSaving] = useState(false);

  const columnsQuery = useQuery({
    queryKey: ['columns', projectId],
    queryFn: () => api.listColumns(projectId!),
    enabled: !!projectId,
  });

  const [columns, setColumnsState] = useState<LocalColumn[]>([]);
  // Dirty guard (review rounds 2+3): the intake toggle invalidates
  // ['columns'] to refresh the marker display, and an unconditional sync
  // would wipe a mid-rename/reorder the user hadn't saved. But the guard
  // alone created a worse hole: with stale-dirty local state, enabling
  // intake (which creates a column server-side) followed by "Save
  // columns" would DELETE the new intake column — replace-columns treats
  // omission as deletion. So the toggle is disabled while dirty (state,
  // not a ref, because the checkbox must react), and the intake display
  // reads from SERVER truth below, never local state.
  //
  // Edited, and still different from what is saved: an edit undone by hand
  // (a box ticked and unticked) no longer leaves the toggle disabled until a
  // save nobody needed (Jason, 23 Oct, t-ULisWa).
  const [columnsEdited, setColumnsEdited] = useState(false);
  const columnsEditedRef = useRef(columnsEdited);
  columnsEditedRef.current = columnsEdited;
  /**
   * A waiting-stage change out (TOADO-970, review round 5): a columns save
   * waits for it, since its board would not have the change, and the change
   * waits for a save (`blocked`), so the two never cross.
   */
  const [stageBusy, setStageBusy] = useState(false);
  const withStageBusy = async (change: () => Promise<void>) => {
    setStageBusy(true);
    try {
      await change();
    } finally {
      setStageBusy(false);
    }
  };
  const columnsDirty = columnsEdited && !sameColumns(columns, columnsQuery.data);
  /**
   * A columns save out (review round 6), from its request until the saved
   * columns are read back: a stage change waits for it, since the save's board
   * does not have the change and would take it away again.
   */
  const [columnsSaving, setColumnsSaving] = useState(false);
  const [intakeToggling, setIntakeToggling] = useState(false);
  const setColumns = (next: LocalColumn[]) => { setColumnsEdited(true); setColumnsState(next); };
  // Back to the saved columns once the edits match them, so the next load is taken in.
  useEffect(() => {
    if (columnsEdited && !columnsDirty) setColumnsEdited(false);
  }, [columnsEdited, columnsDirty]);
  const [patterns, setPatterns] = useState<string[]>([]);
  const [deleteOpen, setDeleteOpen] = useState(false);

  useEffect(() => {
    if (columnsQuery.data && !columnsEdited) {
      setColumnsState(columnsQuery.data.map((c) => ({ id: c.id, name: c.name, isIntake: c.isIntake, isTerminal: c.isTerminal, waitingOnReporter: c.waitingOnReporter })));
    }
  }, [columnsQuery.data, columnsEdited]);
  // On the patterns alone (TOADO-998 review round 3): a save of anything else in the project, the
  // instructions for agents say, must not put back URL patterns being edited. The query keeps an
  // unchanged list the same list, so this runs only when the saved patterns change.
  const savedPatterns = project?.urlPatterns;
  useEffect(() => {
    if (savedPatterns) setPatterns(savedPatterns);
  }, [savedPatterns]);

  if (!project) return null;

  // group: 'widget' entries render indented under a "Widget" label.
  // Owner-only: every widget route is requireOwner, so showing the tabs
  // to a member just walks them into a 403 that reads like "this project
  // has no widget".
  const tabs: Array<{ key: Tab; label: string; group?: 'extension' | 'widget' }> = [
    { key: 'general',  label: 'General' },
    { key: 'columns',  label: 'Columns' },
    // How this board works, beside the columns it explains (TOADO-987).
    { key: 'workflow', label: 'Board workflow' },
    { key: 'ticket-types', label: 'Ticket types' },
    // First in the group because it is the one that explains the other two,
    // and because somebody who has not installed the extension needs it
    // before either of them means anything.
    { key: 'extension', label: 'How capture works', group: 'extension' },
    // Both of these are read by the extension and nothing else: the patterns
    // are matched in the extension's own project cache, and the recording mode
    // is read when somebody presses Record.
    { key: 'urls',     label: 'URL patterns', group: 'extension' },
    { key: 'recording', label: 'Walkthrough recording', group: 'extension' },
    ...(isOwner ? [
      { key: 'widget' as const,            label: 'Install',    group: 'widget' as const },
      { key: 'widget-appearance' as const, label: 'Appearance', group: 'widget' as const },
      // Capture before Questions (Jason, 23 Oct).
      { key: 'widget-capture' as const,    label: 'Capture',    group: 'widget' as const },
      { key: 'widget-questions' as const,  label: 'Questions',  group: 'widget' as const },
      { key: 'widget-security' as const,   label: 'Security',   group: 'widget' as const },
    ] : []),
    /**
     * Bug intake decides where widget submissions land, so it reads as part
     * of that group rather than as a column setting.
     *
     * Outside the owner-only block above, though, because unlike the rest of
     * the group it is not owner-only and moving it inside would quietly take
     * it away from members. Sitting here keeps it next to the widget entries
     * for an owner and makes it the only one in the group for everybody else,
     * which also keeps the group heading from appearing twice: that heading
     * renders before the first entry whose predecessor is not in the group,
     * so an entry separated from the others by Jira export would have grown a
     * second one.
     */
    { key: 'intake', label: 'Bug intake', group: 'widget' },
    ...(isOwner ? [
      { key: 'jira' as const, label: 'Jira export' },
    ] : []),
    { key: 'archival', label: 'Archival' },
    { key: 'danger',   label: 'Danger zone' },
  ];

  const saveGeneral = async (name: string, description: string, color: string, repositoryUrl: string) => {
    try {
      await api.updateProject(projectId!, { name, description: description || null, color, repositoryUrl: repositoryUrl.trim() || null });
      await qc.invalidateQueries({ queryKey: ['project', projectId] });
      await qc.invalidateQueries({ queryKey: ['projects', project.companyId] });
      toast.push({ variant: 'success', title: 'Project updated' });
    } catch (e) {
      toast.push({ variant: 'danger', title: 'Save failed', description: (e as Error).message });
    }
  };

  // TOADO-383: what a walkthrough of this project records. Saved on the
  // click rather than behind the General tab's Save button, because it is a
  // discrete choice and not a field being drafted - the same reason the
  // intake toggle saves immediately.
  const saveRecordingMode = async (mode: 'video' | 'audio' | null) => {
    try {
      await saveProjectRecordingMode(qc, { id: projectId!, companyId: project.companyId }, mode);
      toast.push({ variant: 'success', title: 'Recording setting saved' });
    } catch (e) {
      toast.push({ variant: 'danger', title: 'Save failed', description: (e as Error).message });
    }
  };

  const saveUrls = async () => {
    try {
      await api.updateProject(projectId!, { urlPatterns: patterns });
      await qc.invalidateQueries({ queryKey: ['project', projectId] });
      readIconSoon(qc, projectId!, project?.companyId);
      toast.push({ variant: 'success', title: 'URL patterns saved' });
    } catch (e) {
      toast.push({ variant: 'danger', title: 'Save failed', description: (e as Error).message });
    }
  };

  // TOADO-251: the bug-intake toggle. The server owns adopt-or-create of
  // the marked column; the client just flips the flag and refetches both
  // the project (toggle state) and the columns (a column may have been
  // created or adopted).
  /**
   * The board the stage route answered with (TOADO-970). Columns edited since
   * the request began are kept, with the new column and the flag put into
   * them, so saving that draft does not take the change back (review round 3).
   */
  const adoptWaitingStage = (answer: { waitingColumnId: string; created: boolean; columns: Column[] }) => {
    qc.setQueryData(['columns', projectId], answer.columns);
    // The board is then read again (TOADO-977 review rounds 14 and 15). A read still out
    // began before this change and would land over it, and a columns save would then
    // delete the new column; invalidating cancels it and starts one that sees the change.
    // Not a bare cancel: the answer's board can itself be older than another change (Bug
    // intake switched on while it was out), which only a read after both can show. A
    // caller awaiting the cancelled read is handed the new one's answer. Every such read, on
    // screen or not: a project just left keeps its late read otherwise (review round 16).
    void qc.invalidateQueries({ queryKey: ['columns', projectId], exact: true, refetchType: 'all' });
    // Whether the draft is edited now, when the answer lands: a draft begun
    // while the request was out is the one a save would send (review round 4).
    if (!columnsEditedRef.current) return;
    setColumnsState((draft) => {
      const added = answer.columns.find((c) => c.id === answer.waitingColumnId);
      const next = draft.map((c) => (c.id === answer.waitingColumnId ? { ...c, waitingOnReporter: true } : c));
      // A column marked that the draft has taken out stays out: the draft is the person's.
      if (!answer.created || !added || next.some((c) => c.id === added.id)) return next;
      // Where it stands on the board: after the column before it there, or at the end if the draft has lost that one.
      const before = answer.columns[answer.columns.indexOf(added) - 1];
      const after = before ? next.findIndex((c) => c.id === before.id) : -1;
      next.splice(after >= 0 ? after + 1 : next.length, 0, { id: added.id, name: added.name, isIntake: added.isIntake, waitingOnReporter: true });
      return next;
    });
  };

  const toggleIntake = async (enabled: boolean) => {
    // Bug intake and a waiting-stage change each rewrite the board, so neither starts while the
    // other is out: an answer read before the other's change would be the board installed (review round 16).
    if (stageBusy) return;
    setIntakeToggling(true);
    try {
      await api.updateProject(projectId!, { bugIntakeEnabled: enabled });
      await Promise.all([
        qc.invalidateQueries({ queryKey: ['project', projectId] }),
        qc.invalidateQueries({ queryKey: ['columns', projectId] }),
      ]);
      toast.push({
        variant: 'success',
        title: enabled ? 'Bug intake enabled' : 'Bug intake disabled',
        description: enabled
          ? 'Incoming widget reports now land in the Bug intake column.'
          : 'Widget reports go to the first column again. An empty intake column is removed; one holding tickets stays.',
      });
    } catch (err) {
      toast.push({ variant: 'danger', title: 'Could not update bug intake', description: String(err) });
    } finally {
      setIntakeToggling(false);
    }
  };

  const saveColumns = async () => {
    setColumnsSaving(true);
    try {
      await api.replaceColumns(projectId!, columns.map((c, i) => ({ id: c.id, name: c.name, order: i, waitingOnReporter: c.waitingOnReporter === true })));
      // The saved columns are taken in again, new columns' ids included.
      setColumnsEdited(false);
      // Board workflow reads which saved columns are gone from these (review round 4).
      await qc.invalidateQueries({ queryKey: ['columns', projectId] });
      toast.push({ variant: 'success', title: 'Columns saved' });
    } catch (e) {
      toast.push({ variant: 'danger', title: 'Save failed', description: (e as Error).message });
    } finally {
      setColumnsSaving(false);
    }
  };

  const deleteProject = async () => {
    try {
      await api.deleteProject(projectId!);
      await qc.invalidateQueries({ queryKey: ['projects', project.companyId] });
      toast.push({ variant: 'success', title: 'Project deleted' });
      navigate('/');
    } catch (e) {
      toast.push({ variant: 'danger', title: 'Delete failed', description: (e as Error).message });
    }
  };

  return (
    // The widget panels put a live preview beside their controls, so they
    // get the extra width; every other tab is prose and form rows.
    //
    // Screens, for pins (TOADO-701): each tab's region is named, and the nav
    // and the project header, the same on every tab, are on every screen. The
    // nav needs saying so: it sits inside the app's `main`, where the extension
    // does not count a `nav` as the site's own navigation.
    <div className={cn(styles.page, tab.startsWith('widget') && styles.pageWide)}>
      <nav className={styles.nav} aria-labelledby={navTitleId} data-toado-view={EVERY_SCREEN}>
        <button
          className={pageStyles.backLink}
          onClick={() => guardedNavigate(`/projects/${projectId}/board`)}
        >
          <ArrowLeft size={12} />
          Back to {project.name}
        </button>
        <div className={styles.navTitle} id={navTitleId}>Project settings</div>
        {tabs.map((t, i) => (
          <Fragment key={t.key}>
            {t.group && tabs[i - 1]?.group !== t.group && (
              <div className={styles.navGroupLabel}>{GROUP_LABEL[t.group]}</div>
            )}
            <button
              onClick={() => selectTab(t.key)}
              data-tour-anchor={`settings.${t.key}`}
              className={cn(
                styles.navItem,
                t.group && styles.navSubItem,
                tab === t.key && styles.navActive,
              )}
              // The widget sections' old internal tablist carried
              // aria-selected; the page nav is ordinary navigation, so
              // the equivalent selected-state signal here is
              // aria-current (review round 1).
              aria-current={tab === t.key ? 'page' : undefined}
              style={{ textAlign: 'left' }}
            >
              {t.label}
            </button>
          </Fragment>
        ))}
      </nav>

      <main className={styles.content}>
        {/* Persistent project header: keeps the project name + color
            visible across every tab so users always know which project
            they're editing. */}
        <div className={pageStyles.projectHeader} data-toado-view={EVERY_SCREEN}>
          <ProjectMark project={project} size={32} className={pageStyles.projectMark} />
          <div className={pageStyles.projectHeaderText}>
            <span className={pageStyles.projectEyebrow}>Project settings</span>
            <h1 className={pageStyles.projectTitle}>
              <InlineProjectName
                projectId={project.id}
                companyId={project.companyId}
                name={project.name}
              />
            </h1>
          </div>
          {WIDGET_SECTION_BY_TAB[tab] && isOwner && (
            <div className={pageStyles.projectHeaderActions}>
              <Button
                variant="secondary"
                size="sm"
                onClick={() => setWizardRequest((count) => count + 1)}
                disabled={widgetDirty}
                title={widgetDirty ? 'Save or discard your widget changes first' : undefined}
              >
                Run the setup wizard
              </Button>
              {/* Try it live and the widget's status, put here by the widget editor (Jason, 23 Oct). */}
              <span className={pageStyles.headerDivider} aria-hidden="true" />
              <div ref={setWidgetHeaderSlot} className={pageStyles.headerSlot} data-testid="widget-header-slot" />
            </div>
          )}
        </div>

        {/* One region for every tab, its name following the tab: the same
            element throughout, so the widget editor below stays mounted
            across its four tabs as it always has. */}
        {/* The tab's content, which the workspace tour points at section by section (Jason, 28 Oct). */}
        <div data-toado-view={TAB_SCREEN[tab]} data-tour-anchor={`settings.page.${tab}`}>
          {tab === 'general' && <GeneralTab project={project} onSave={saveGeneral} />}
          {tab === 'extension' && (
            <ExtensionCaptureTab onOpenTab={selectTab} onSetupWidget={isOwner ? openWidgetSetup : undefined} />
          )}
          {tab === 'recording' && (
            <WalkthroughRecordingTab
              mode={project.recordingMediaMode ?? null}
              onChangeMode={saveRecordingMode}
            />
          )}
          {tab === 'intake' && (
            <IntakeTab
              intakeEnabled={project.bugIntakeEnabled ?? false}
              onToggleIntake={toggleIntake}
              columnsDirty={columnsDirty}
              serverIntakeColumnName={columnsQuery.data?.find((c) => c.isIntake)?.name ?? null}
              toggling={intakeToggling || stageBusy}
              columns={columnsQuery.data}
            />
          )}
          {tab === 'columns' && (
            <ColumnsTab
              columns={columns}
              onChange={setColumns}
              onSave={saveColumns}
              onDiscard={() => setColumnsEdited(false)}
              intakeEnabled={project.bugIntakeEnabled ?? false}
              onToggleIntake={toggleIntake}
              columnsDirty={columnsDirty}
              serverIntakeColumnName={columnsQuery.data?.find((c) => c.isIntake)?.name ?? null}
              savedColumns={columnsQuery.data}
              saveHeld={stageBusy}
              saving={columnsSaving}
            />
          )}
          {tab === 'workflow' && (
            <BoardWorkflow
              projectName={project.name}
              columns={columnsQuery.data ?? []}
              intakeOn={project.bugIntakeEnabled ?? false}
              onEditColumns={() => selectTab('columns')}
              // A viewer cannot change the board's columns, so is told who can (TOADO-970).
              waitingStage={company && company.role !== 'viewer' ? {
                add: () => withStageBusy(async () => adoptWaitingStage(await api.setWaitingStage(projectId!, { add: true }))),
                mark: (columnId) => withStageBusy(async () => adoptWaitingStage(await api.setWaitingStage(projectId!, { columnId }))),
                blocked: columnsSaving ? 'saving' : intakeToggling ? 'intake' : columnsDirty ? 'unsaved' : undefined,
              } : undefined}
              // The project's instructions for agents (TOADO-998); a viewer reads them only.
              instructions={{
                saved: project.agentInstructions ?? null,
                canEdit: !!company && company.role !== 'viewer',
                draft: agentDraft,
                onDraft: setAgentDraft,
                saving: agentSaving,
                // Saving is last-write-wins, on purpose: the page says who saved last, and the change
                // is in the audit log. Read again after, so a late answer cannot leave older text shown.
                save: async (text) => {
                  setAgentSaving(true);
                  try {
                    const next = await api.updateProject(projectId!, { agentInstructions: text });
                    qc.setQueryData(['project', projectId], next);
                    void qc.invalidateQueries({ queryKey: ['project', projectId], exact: true });
                    return next.agentInstructions ?? null;
                  } finally {
                    setAgentSaving(false);
                  }
                },
              }}
            />
          )}
          {tab === 'ticket-types' && (
            <ProjectTicketTypesTab projectId={projectId!} companyId={project.companyId} isOwner={isOwner} />
          )}
          {tab === 'urls' && (
            <UrlsTab
              patterns={patterns}
              onChange={setPatterns}
              onSave={saveUrls}
              canSetupWidget={isOwner}
              onSetupWidget={openWidgetSetup}
            />
          )}
          {/* One WidgetTab instance serves all four widget-* tabs. It stays
              mounted while switching among them, so a staged, unsaved draft
              (an allowlist edit, a color change) survives moving between
              Install / Appearance / Capture / Security exactly as it did
              when they were internal sections of one tab. */}
          {WIDGET_SECTION_BY_TAB[tab] && isOwner && (
            <Suspense fallback={<p className={pageStyles.loadingNote}>Loading widget…</p>}>
              <WidgetTab
                projectId={projectId!}
                section={WIDGET_SECTION_BY_TAB[tab]}
                wizardRequest={wizardRequest}
                onDirtyChange={setWidgetDirty}
                headerSlot={widgetHeaderSlot}
              />
            </Suspense>
          )}
          {tab === 'jira' && isOwner && <JiraMappingTab projectId={projectId!} companyId={project.companyId} />}
          {tab === 'archival' && (
            <ArchivalTab
              value={project.archivalDays ?? null}
              onSave={async (days) => {
                try {
                  await api.updateProject(projectId!, { archivalDays: days });
                  await qc.invalidateQueries({ queryKey: ['project', projectId] });
                  toast.push({ variant: 'success', title: 'Archival policy saved' });
                } catch (e) {
                  toast.push({ variant: 'danger', title: 'Save failed', description: (e as Error).message });
                }
              }}
            />
          )}
          {tab === 'danger' && <DangerTab projectName={project.name} onDelete={() => setDeleteOpen(true)} />}
        </div>
      </main>

      <DestructiveConfirm
        open={deleteOpen}
        onClose={() => setDeleteOpen(false)}
        onConfirm={deleteProject}
        title={`Delete ${project.name}?`}
        description={`This will soft-delete ${project.name} and hide it from the board. Tickets are preserved for 30 days before hard-delete.`}
        typeToConfirm={project.name}
        confirmLabel="Delete project"
      />
    </div>
  );
}

function GeneralTab({
  project, onSave,
}: {
  project: Project;
  onSave: (name: string, description: string, color: string, repositoryUrl: string) => void;
}) {
  const [name, setName] = useState(project.name);
  const [description, setDescription] = useState(project.description ?? '');
  const [color, setColor] = useState(project.color);
  const [repositoryUrl, setRepositoryUrl] = useState(project.repositoryUrl ?? '');
  const [saving, setSaving] = useState(false);
  useEffect(() => {
    setName(project.name);
    setDescription(project.description ?? '');
    setColor(project.color);
    setRepositoryUrl(project.repositoryUrl ?? '');
  }, [project.name, project.description, project.color, project.repositoryUrl]);

  const handleSave = async () => {
    setSaving(true);
    await onSave(name, description, color, repositoryUrl);
    setSaving(false);
  };
  // An address the server would refuse is said here, before Save (TOADO-844).
  const repositoryProblem = repositoryUrl.trim() !== '' && !webLink(repositoryUrl.trim())
    ? 'An address starting with https://, such as https://github.com/acme/shop.'
    : undefined;
  const dirty =
    name !== project.name ||
    description !== (project.description ?? '') ||
    color.toLowerCase() !== project.color.toLowerCase() ||
    repositoryUrl.trim() !== (project.repositoryUrl ?? '');

  return (
    <>
      <h2 className={styles.h1}>General</h2>
      <p className={styles.sub}>Name, description, and basic configuration.</p>
      <section className={styles.section}>
        <FormField label="Name"><Input value={name} onChange={(e) => setName(e.target.value)} /></FormField>
        <div style={{ height: 12 }} />
        <FormField label="Description">
          <Input value={description} onChange={(e) => setDescription(e.target.value)} placeholder="What this project covers" />
        </FormField>
        <div style={{ height: 12 }} />
        <FormField label="Color">
          <ProjectColorPicker value={color} onChange={setColor} />
        </FormField>
        <div style={{ height: 12 }} />
        <FormField label="Icon" help="Shown wherever the project is named. Toado looks for your site's own icon each week, from the project's URL patterns.">
          <ProjectIconField key={project.id} project={{ ...project, color }} />
        </FormField>
        <div style={{ height: 12 }} />
        <FormField
          label="Code repository"
          help="Where this project's code lives. A fix an agent records shows its commit as a link into it."
          error={repositoryProblem}
        >
          <Input value={repositoryUrl} onChange={(e) => setRepositoryUrl(e.target.value)} placeholder="https://github.com/acme/shop" />
        </FormField>
        <div style={{ height: 16 }} />
        <Button variant="primary" disabled={!dirty || !name.trim() || saving || repositoryProblem !== undefined} loading={saving} onClick={handleSave}>Save</Button>
      </section>
    </>
  );
}

function ColumnsTab({
  columns, onChange, onSave, onDiscard, intakeEnabled, onToggleIntake, columnsDirty, serverIntakeColumnName, savedColumns, saveHeld = false, saving = false,
}: {
  columns: LocalColumn[];
  onChange: (c: LocalColumn[]) => void;
  onSave: () => void;
  /** Back to the columns as saved. */
  onDiscard: () => void;
  intakeEnabled: boolean;
  onToggleIntake: (enabled: boolean) => Promise<void>;
  /** Unsaved local column edits pending — the intake toggle is disabled
   *  while true (review round 3: toggling with stale-dirty state, then
   *  saving, deleted the freshly created intake column). */
  columnsDirty: boolean;
  /** SERVER truth for the marked column's name; local state may be
   *  mid-edit and must not drive the warning. */
  serverIntakeColumnName: string | null;
  /** The columns as saved, for the intake drawing; undefined while they load. */
  savedColumns: Column[] | undefined;
  /** A waiting-stage change is out (TOADO-970): saving now would take it back, so it waits. */
  saveHeld?: boolean;
  /**
   * The page's own columns save, out (review round 7): held by the page, so a
   * remount of this tab still shows it, and its Save buttons stay disabled until
   * it is in, which is what keeps a second save from overlapping it.
   */
  saving?: boolean;
}) {
  const [togglingIntake, setTogglingIntake] = useState(false);
  const handleToggleIntake = async (next: boolean) => {
    setTogglingIntake(true);
    try { await onToggleIntake(next); } finally { setTogglingIntake(false); }
  };
  const [editing, setEditing] = useState<string | null>(null);
  const [draft, setDraft] = useState('');
  const [activeId, setActiveId] = useState<string | null>(null);

  // `distance: 4` so clicking the drag handle or the card body doesn't kick
  // off a drag on every micro-move — matches the board's ticket-drag feel.
  const sensors = useSensors(useSensor(PointerSensor, { activationConstraint: { distance: 4 } }));

  const startEdit = (col: LocalColumn) => { setEditing(col.id); setDraft(col.name); };
  const commit = () => {
    if (editing) {
      onChange(columns.map((c) => (c.id === editing ? { ...c, name: draft.trim() || c.name } : c)));
    }
    setEditing(null);
  };
  const cancel = () => setEditing(null);
  const remove = (id: string) => onChange(columns.filter((c) => c.id !== id));
  const add = () => {
    if (columns.length >= MAX_COLUMNS) return;
    const id = 'c-' + Math.random().toString(36).slice(2, 6);
    onChange([...columns, { id, name: 'New column' }]);
    setEditing(id);
    setDraft('New column');
  };
  const handleSave = () => { void onSave(); };

  const onDragStart = (e: DragStartEvent) => setActiveId(String(e.active.id));
  const onDragEnd = (e: DragEndEvent) => {
    setActiveId(null);
    const activeKey = String(e.active.id);
    const overKey = e.over ? String(e.over.id) : null;
    if (!overKey || activeKey === overKey) return;
    const from = columns.findIndex((c) => c.id === activeKey);
    const to = columns.findIndex((c) => c.id === overKey);
    if (from < 0 || to < 0) return;
    onChange(arrayMove(columns, from, to));
  };

  const activeCol = activeId ? columns.find((c) => c.id === activeId) : null;
  const atCap = columns.length >= MAX_COLUMNS;

  return (
    <>
      <h2 className={styles.h1}>Columns</h2>
      <p className={styles.sub}>
        Columns define the workflow stages on the board. Drag to reorder, click the name to rename,
        click the <Trash2 size={12} style={{ verticalAlign: '-2px' }} /> to remove. New projects default to
        To Do / In Progress / In Review / Done. You can add up to {MAX_COLUMNS} columns.
      </p>
      <section className={styles.section}>
        <DndContext
          sensors={sensors}
          collisionDetection={closestCenter}
          onDragStart={onDragStart}
          onDragEnd={onDragEnd}
          onDragCancel={() => setActiveId(null)}
        >
          <SortableContext items={columns.map((c) => c.id)} strategy={horizontalListSortingStrategy}>
            <div className={pageStyles.colsBoard}>
              {columns.map((c, i) => (
                <SortableColumnCard
                  key={c.id}
                  column={c}
                  index={i}
                  isEditing={editing === c.id}
                  draft={draft}
                  setDraft={setDraft}
                  onStartEdit={() => startEdit(c)}
                  onCommit={commit}
                  onCancel={cancel}
                  onRemove={() => remove(c.id)}
                  canRemove={columns.length > 1}
                  onToggleWaiting={(waiting) => onChange(columns.map((col) => (col.id === c.id ? { ...col, waitingOnReporter: waiting } : col)))}
                />
              ))}
              <button
                type="button"
                className={pageStyles.colAddBtn}
                onClick={add}
                disabled={atCap}
                title={atCap ? `Max ${MAX_COLUMNS} columns` : 'Add column'}
              >
                <Plus size={14} />
                <span>Add column</span>
              </button>
            </div>
          </SortableContext>

          {createPortal(
            <DragOverlay dropAnimation={{ duration: 160, easing: 'cubic-bezier(0.2, 0, 0, 1)' }}>
              {activeCol ? (
                <div className={cn(pageStyles.colCard, pageStyles.colCardOverlay)}>
                  <div className={pageStyles.colHead}>
                    <span className={pageStyles.colDragHandle} aria-hidden><GripVertical size={14} /></span>
                    <span className={pageStyles.colName}>{activeCol.name}</span>
                  </div>
                </div>
              ) : null}
            </DragOverlay>,
            document.body,
          )}
        </DndContext>

        <div className={pageStyles.colHint}>
          {columns.length} / {MAX_COLUMNS} columns. Deleting a column moves its tickets to the leftmost remaining column.
        </div>

        <div style={{ height: 16 }} />
        <Button variant="primary" loading={saving} disabled={saveHeld} onClick={handleSave}>Save columns</Button>
        {saveHeld && <span className={styles.sub} role="status" data-testid="columns-save-held">A column is being added on Board workflow; save once it is in.</span>}
      </section>

      <section className={styles.section} data-testid="bug-intake-section">
        {/* The large Off/On switch (Jason, 10 Oct), named by the line beside it. */}
        <div style={{ display: 'flex', alignItems: 'flex-start', gap: 14 }}>
          {columnsDirty ? (
            // Why it is off, at once: the tip is on the wrapper Tooltip draws, which still takes the hover.
            <Tooltip content="Save or discard your column changes first: this adds or removes a column." delay={0} multiline>
              <BigSwitch labelledBy="columns-intake-title" checked={intakeEnabled} disabled onChange={() => {}} />
            </Tooltip>
          ) : (
            <BigSwitch
              labelledBy="columns-intake-title"
              checked={intakeEnabled}
              // Not while a waiting-stage change is out (saveHeld): both rewrite the board (review round 16).
              disabled={togglingIntake || saveHeld}
              onChange={(on) => { void handleToggleIntake(on); }}
            />
          )}
          <span style={{ paddingTop: 6 }}>
            <strong id="columns-intake-title">Bug intake column</strong>
            <span className={styles.sub} style={{ display: 'block', marginBottom: 0 }}>
              Route incoming widget reports to a dedicated column for triage. The Bug intake tab
              explains exactly what changes when this flips.
            </span>
            {columnsDirty && (
              <span className={pageStyles.intakeBlocked} role="status" data-testid="intake-blocked">
                <span>
                  Your column changes are not saved yet. Turning bug intake on or off adds or removes a
                  column, so save or discard them first.
                </span>
                <span className={pageStyles.intakeBlockedActions}>
                  <Button size="sm" variant="primary" loading={saving} disabled={saveHeld} onClick={handleSave}>Save columns</Button>
                  <Button size="sm" variant="ghost" onClick={onDiscard}>Discard changes</Button>
                </span>
              </span>
            )}
            {intakeEnabled && serverIntakeColumnName !== null && (
              <span className={styles.sub} style={{ display: 'block', marginBottom: 0 }}>
                Reports currently land in <strong>{serverIntakeColumnName}</strong>. Renaming or moving that
                column is fine: routing follows the column, not its name or position.
              </span>
            )}
            {intakeEnabled && serverIntakeColumnName === null && (
              <span style={{ display: 'block', color: 'var(--danger)' }}>
                Bug intake is enabled but its column was deleted, so reports are falling back to the
                first work column. Turn the toggle off and on again to recreate it.
              </span>
            )}
          </span>
        </div>
        {/* The Bug intake tab's drawing of this board, so what the switch does
            shows here too (Jason, 10 Oct). Drawn from the saved columns, as
            there, not from edits not yet saved. */}
        <div style={{ height: 12 }} />
        <IntakeBoardDrawing
          columns={savedColumns}
          intakeEnabled={intakeEnabled}
          intakeColumnName={serverIntakeColumnName}
        />
      </section>
    </>
  );
}

interface SortableColumnCardProps {
  column: LocalColumn;
  index: number;
  isEditing: boolean;
  draft: string;
  setDraft: (v: string) => void;
  onStartEdit: () => void;
  onCommit: () => void;
  onCancel: () => void;
  onRemove: () => void;
  canRemove: boolean;
  /** Marks the column as waiting on whoever reported its tickets (TOADO-723). */
  onToggleWaiting: (waiting: boolean) => void;
}

function SortableColumnCard({
  column, index, isEditing, draft, setDraft,
  onStartEdit, onCommit, onCancel, onRemove, canRemove, onToggleWaiting,
}: SortableColumnCardProps) {
  const { attributes, listeners, setNodeRef, transform, transition, isDragging } = useSortable({ id: column.id });
  const style: React.CSSProperties = {
    transform: CSS.Transform.toString(transform),
    transition,
  };

  return (
    <div
      ref={setNodeRef}
      style={style}
      className={cn(pageStyles.colCard, isDragging && pageStyles.dragging)}
    >
      <div className={pageStyles.colHead}>
        {/* Drag handle is the ONLY element that gets the dnd listeners — the
            rest of the card (name field, delete button) stays clickable. */}
        <span
          className={pageStyles.colDragHandle}
          {...attributes}
          {...listeners}
          aria-label="Drag to reorder"
        >
          <GripVertical size={14} />
        </span>
        {isEditing ? (
          <Input
            autoFocus
            value={draft}
            onChange={(e) => setDraft(e.target.value)}
            onBlur={onCommit}
            onKeyDown={(e) => {
              if (e.key === 'Enter') { e.preventDefault(); onCommit(); }
              if (e.key === 'Escape') { e.preventDefault(); onCancel(); }
            }}
          />
        ) : (
          <button
            type="button"
            className={pageStyles.colName}
            onClick={onStartEdit}
            title="Click to rename"
            style={{ background: 'transparent', border: 0, textAlign: 'left', padding: 0, cursor: 'text' }}
          >
            {column.name}
          </button>
        )}
        <button
          type="button"
          className={pageStyles.colRemove}
          onClick={onRemove}
          disabled={!canRemove}
          aria-label="Delete column"
          title={canRemove ? 'Delete column' : 'A project needs at least one column'}
        >
          <Trash2 size={14} />
        </button>
      </div>
      <div className={pageStyles.colMeta}>#{index + 1}</div>
      {/* What an AI agent does with this column, too (board ticket t-E7ly4Q): the
          Toado skill's request_info asks the reporter and moves the ticket here. */}
      <Tooltip content="Tickets here are waiting on the person who reported them. When an AI agent working a ticket needs something from the reporter, it asks them in a comment and moves the ticket here. The Dashboard lists the reporter's tickets here under Waiting on your reply." delay={0} multiline>
        <label className={pageStyles.colMeta}>
          <input
            type="checkbox"
            checked={column.waitingOnReporter === true}
            // The done column cannot be both (review round 6): its tickets are finished.
            disabled={column.isTerminal === true && column.waitingOnReporter !== true}
            onChange={(e) => onToggleWaiting(e.target.checked)}
          />{' '}
          Waiting on the reporter
        </label>
      </Tooltip>
      {column.isTerminal === true && (
        <div className={pageStyles.colMeta} data-testid="column-done-note">Tickets finish here</div>
      )}
    </div>
  );
}

function UrlsTab({
  patterns, onChange, onSave, canSetupWidget, onSetupWidget,
}: {
  patterns: string[];
  onChange: (p: string[]) => void;
  onSave: () => void;
  /** Owners can add the widget; the tab is owner-only. */
  canSetupWidget: boolean;
  onSetupWidget: () => void;
}) {
  const [draft, setDraft] = useState('');
  const [editingIdx, setEditingIdx] = useState<number | null>(null);
  const [saving, setSaving] = useState(false);

  const add = () => {
    const val = draft.trim();
    if (!val) return;
    onChange([...patterns, val]);
    setDraft('');
  };
  const removeAt = (i: number) => onChange(patterns.filter((_, idx) => idx !== i));
  const commitEdit = (i: number, val: string) => {
    onChange(patterns.map((p, idx) => (idx === i ? val : p)));
    setEditingIdx(null);
  };
  const handleSave = async () => { setSaving(true); await onSave(); setSaving(false); };

  return (
    <>
      <h2 className={styles.h1}>URL patterns</h2>
      <p className={styles.sub}>
        URL patterns tell Toado which project to auto-assign a capture to based on the current page URL.
        When a capture’s source URL matches a pattern here, it auto-lands in this project.
      </p>

      <section className={styles.section}>
        <div className={pageStyles.examples}>
          <UrlPatternHelp />
        </div>

        <div className={pageStyles.list}>
          {patterns.map((p, i) => (
            <div key={i} className={pageStyles.row}>
              {editingIdx === i ? (
                <EditingRow
                  value={p}
                  onCancel={() => setEditingIdx(null)}
                  onSave={(v) => commitEdit(i, v)}
                />
              ) : (
                <>
                  <code className={pageStyles.rowName}>{p}</code>
                  <Button variant="ghost" size="sm" leftIcon={<Pencil size={14} />} onClick={() => setEditingIdx(i)}>Edit</Button>
                  <Button variant="ghost" size="sm" leftIcon={<Trash2 size={14} />} onClick={() => removeAt(i)}>Delete</Button>
                </>
              )}
            </div>
          ))}
        </div>
        <div style={{ display: 'flex', gap: 8, marginTop: 12 }}>
          <Input
            value={draft}
            onChange={(e) => setDraft(e.target.value)}
            placeholder="https://myapp.com/*"
            onKeyDown={(e) => { if (e.key === 'Enter') add(); }}
          />
          <Button variant="secondary" leftIcon={<Plus size={14} />} onClick={add}>Add</Button>
        </div>
        <div style={{ height: 16 }} />
        <Button variant="primary" loading={saving} onClick={handleSave}>Save patterns</Button>
      </section>

      {canSetupWidget && (
        <section className={styles.section}>
          <WidgetNudge onSetupWidget={onSetupWidget}>
            Add the report widget with one line of code. Any origin you allow it then
            shows up here automatically, so this list and the widget stay in step.
          </WidgetNudge>
        </section>
      )}
    </>
  );
}

function EditingRow({ value, onSave, onCancel }: { value: string; onSave: (v: string) => void; onCancel: () => void }) {
  const [v, setV] = useState(value);
  return (
    <>
      <Input
        autoFocus
        value={v}
        onChange={(e) => setV(e.target.value)}
        onKeyDown={(e) => {
          if (e.key === 'Enter') onSave(v.trim() || value);
          if (e.key === 'Escape') onCancel();
        }}
      />
      <Button variant="secondary" size="sm" leftIcon={<Check size={14} />} onClick={() => onSave(v.trim() || value)}>Save</Button>
      <Button variant="ghost" size="sm" leftIcon={<X size={14} />} onClick={onCancel}>Cancel</Button>
    </>
  );
}

function ArchivalTab({ value, onSave }: { value: number | null; onSave: (days: number | null) => Promise<void> }) {
  const [enabled, setEnabled] = useState<boolean>(value !== null);
  const [days, setDays] = useState<number>(value ?? 90);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    setEnabled(value !== null);
    setDays(value ?? 90);
  }, [value]);

  const dirty = enabled !== (value !== null) || (enabled && days !== (value ?? 90));

  const handleSave = async () => {
    setSaving(true);
    await onSave(enabled ? days : null);
    setSaving(false);
  };

  return (
    <>
      <h2 className={styles.h1}>Archival</h2>
      <p className={styles.sub}>
        Inactive tickets (no moves, comments, updates, or assignments) are archived automatically.
        This overrides the company default. Individual columns can override further via the columns tab.
      </p>
      <section className={styles.section}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 12, fontSize: 13, marginBottom: 12 }}>
          <BigSwitch labelledBy="archival-enabled-title" checked={enabled} onChange={setEnabled} />
          <span id="archival-enabled-title">Enable per-project archival</span>
        </div>
        <FormField label="Days of inactivity before archive" help="0 archives immediately; typical range 30 to 180.">
          <Input
            type="number"
            value={days}
            onChange={(e) => setDays(Math.max(0, Math.min(365 * 5, Number(e.target.value) || 0)))}
            min={0}
            max={365 * 5}
            disabled={!enabled}
          />
        </FormField>
        <div style={{ height: 16 }} />
        <Button variant="primary" disabled={!dirty || saving} loading={saving} onClick={handleSave}>
          Save
        </Button>
      </section>
    </>
  );
}

function DangerTab({ projectName, onDelete }: { projectName: string; onDelete: () => void }) {
  return (
    <>
      <h2 className={styles.h1} style={{ color: 'var(--danger)' }}>Danger zone</h2>
      <p className={styles.sub}>Deleting a project is permanent and removes all tickets and captures.</p>
      <section className={styles.section} style={{ borderColor: 'var(--danger-border)' }}>
        <Button variant="destructive" leftIcon={<Trash2 size={14} />} onClick={onDelete}>
          Delete {projectName}…
        </Button>
      </section>
    </>
  );
}
